import React, { useEffect, useState } from "react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";

import {
  universityCourseAccordionTriggerClassName,
  universityCourseMutedTextClassName,
} from "./courseOverviewCardShell";
import type { CourseOverviewModule } from "./CourseOverviewView";
import { UniversityCourseLessonCard } from "./UniversityCourseLessonCard";
import { UniversityCourseStatusBadge } from "./UniversityCourseStatusBadge";

type UniversityCourseLessonNavigatorProps = {
  modules: CourseOverviewModule[];
  selectedLesson?: { moduleId: number; lessonId: number } | null;
  isLessonCompleted: (moduleId: number, lessonId: number) => boolean;
  isModuleCompleted: (moduleId: number) => boolean;
  isModuleInProgress: (moduleId: number) => boolean;
  onSelectLesson: (moduleId: number, lessonId: number) => void;
  compact?: boolean;
  className?: string;
};

export function UniversityCourseLessonNavigator({
  modules,
  selectedLesson,
  isLessonCompleted,
  isModuleCompleted,
  isModuleInProgress,
  onSelectLesson,
  compact = false,
  className,
}: UniversityCourseLessonNavigatorProps): React.ReactElement {
  const [openModuleIds, setOpenModuleIds] = useState<string[]>([]);

  useEffect(() => {
    if (!selectedLesson?.moduleId) return;
    const value = `module-${selectedLesson.moduleId}`;
    setOpenModuleIds((prev) => (prev.includes(value) ? prev : [...prev, value]));
  }, [selectedLesson?.moduleId]);

  return (
    <div data-university-course-accordion="" className={cn("min-w-0 space-y-2", className)}>
      <Accordion
        type="multiple"
        value={openModuleIds}
        onValueChange={setOpenModuleIds}
        className="flex w-full flex-col gap-2 border-0"
      >
        {modules.map((module) => (
          <AccordionItem
            key={module.id}
            value={`module-${module.id}`}
            data-university-course-module=""
            className="!border-b min-w-0 rounded-[6px] border border-border bg-card px-3"
          >
            <AccordionTrigger className={universityCourseAccordionTriggerClassName}>
              <div className="flex w-full min-w-0 flex-col gap-2 pr-2 text-left">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    data-university-course-module-title=""
                    className="font-[family-name:var(--font-primary)] text-base font-medium text-[color:var(--brand-tropical-surf)]"
                  >
                    {`Module ${module.id}: ${module.title}`}
                  </span>
                  {isModuleCompleted(module.id) ? (
                    <UniversityCourseStatusBadge tone="completed">Completed</UniversityCourseStatusBadge>
                  ) : null}
                  {!isModuleCompleted(module.id) && isModuleInProgress(module.id) ? (
                    <UniversityCourseStatusBadge tone="progress">In progress</UniversityCourseStatusBadge>
                  ) : null}
                </div>
                {module.description ? (
                  <p
                    data-university-course-muted=""
                    className={`text-sm ${universityCourseMutedTextClassName}`}
                  >
                    {module.description}
                  </p>
                ) : null}
                {module.duration ? (
                  <p
                    data-university-course-muted=""
                    className={`text-xs ${universityCourseMutedTextClassName}`}
                  >
                    {module.duration}
                  </p>
                ) : null}
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-0 pb-4">
              <div className={cn("flex flex-col", compact ? "gap-2" : "gap-3")}>
                {module.lessons.length > 0 ? (
                  module.lessons.map((lesson) => (
                    <UniversityCourseLessonCard
                      key={lesson.id}
                      lesson={lesson}
                      completed={isLessonCompleted(module.id, lesson.id)}
                      compact={compact}
                      selected={
                        selectedLesson?.moduleId === module.id &&
                        selectedLesson?.lessonId === lesson.id
                      }
                      onClick={() => onSelectLesson(module.id, lesson.id)}
                    />
                  ))
                ) : (
                  <div
                    className={`rounded-[6px] border border-dashed border-border p-4 text-center ${universityCourseMutedTextClassName}`}
                  >
                    Lessons will be added soon. Check back later.
                  </div>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
